import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertTriangle } from 'lucide-react';
interface FlashBonusBoxProps {
  onActiveChange: (isActive: boolean) => void;
}
type BonusState = 'active_15' | 'expired_15' | 'active_5' | 'dead';
export function FlashBonusBox({
  onActiveChange
}: FlashBonusBoxProps) {
  const [bonusState, setBonusState] = useState<BonusState | null>(null);
  const [timeLeft, setTimeLeft] = useState<{
    minutes: number;
    seconds: number;
  } | null>(null);
  useEffect(() => {
    const checkState = () => {
      const status = localStorage.getItem('flash_bonus_status') as BonusState | null;
      const endTimeStr = localStorage.getItem('flash_bonus_end_time');
      const now = Date.now();
      if (!status || !endTimeStr) {
        // First visit
        const endTime = now + 15 * 60 * 1000;
        localStorage.setItem('flash_bonus_status', 'active_15');
        localStorage.setItem('flash_bonus_end_time', endTime.toString());
        setBonusState('active_15');
        onActiveChange(true);
        return endTime;
      }
      const endTime = parseInt(endTimeStr, 10);
      if (status === 'active_15') {
        if (now >= endTime) {
          localStorage.setItem('flash_bonus_status', 'expired_15');
          setBonusState('expired_15');
          onActiveChange(false);
          return null;
        }
        setBonusState('active_15');
        onActiveChange(true);
        return endTime;
      }
      if (status === 'expired_15') {
        // Returned after 15 min expired -> start 5 min second chance
        const newEndTime = now + 5 * 60 * 1000;
        localStorage.setItem('flash_bonus_status', 'active_5');
        localStorage.setItem('flash_bonus_end_time', newEndTime.toString());
        setBonusState('active_5');
        onActiveChange(true);
        return newEndTime;
      }
      if (status === 'active_5') {
        if (now >= endTime) {
          localStorage.setItem('flash_bonus_status', 'dead');
          setBonusState('dead');
          onActiveChange(false);
          return null;
        }
        setBonusState('active_5');
        onActiveChange(true);
        return endTime;
      }
      if (status === 'dead') {
        setBonusState('dead');
        onActiveChange(false);
        return null;
      }
      return null;
    };
    let targetEndTime = checkState();
    if (!targetEndTime) return;
    const timer = setInterval(() => {
      const now = Date.now();
      const diff = targetEndTime! - now;
      if (diff <= 0) {
        // Timer reached 0
        const currentStatus = localStorage.getItem('flash_bonus_status');
        if (currentStatus === 'active_15') {
          localStorage.setItem('flash_bonus_status', 'expired_15');
          setBonusState('expired_15');
          onActiveChange(false);
        } else if (currentStatus === 'active_5') {
          localStorage.setItem('flash_bonus_status', 'dead');
          setBonusState('dead');
          onActiveChange(false);
        }
        setTimeLeft(null);
        clearInterval(timer);
      } else {
        setTimeLeft({
          minutes: Math.floor(diff / 1000 / 60 % 60),
          seconds: Math.floor(diff / 1000 % 60)
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [onActiveChange]);
  if (bonusState === 'dead' || bonusState === 'expired_15' || !timeLeft) {
    return null;
  }
  const isSecondChance = bonusState === 'active_5';
  const formatNumber = (num: number) => num.toString().padStart(2, '0');
  return <motion.div initial={{
    opacity: 0,
    y: 20,
    scale: 0.95
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} className="bg-gradient-to-br from-[#FFF9C4] to-[#FFE0B2] border-4 border-[#E65100] rounded-2xl p-5 md:p-8 mb-8 shadow-[0_10px_30px_rgba(230,81,0,0.2)] relative overflow-hidden" data-id="element-278">
      {/* Shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_2s_infinite] pointer-events-none" data-id="element-279" />

      <div className="relative z-10" data-id="element-280">
        <div className="flex justify-center mb-3" data-id="element-281">
          <span className="bg-[#E65100] text-white text-xs md:text-sm font-black px-4 py-1.5 rounded-full shadow-md flex items-center gap-2 uppercase tracking-wider animate-pulse" data-id="element-282">
            <AlertTriangle className="w-4 h-4" data-id="element-283" />
            {isSecondChance ? 'Segunda y última oportunidad' : 'Bono de acción rápida'}
          </span>
        </div>

        <h3 className="text-center text-[#D84315] font-black text-xl md:text-2xl mb-2 leading-tight" data-id="element-284">
          {isSecondChance ? 'Segunda y última oportunidad para llevarte tu bono gratis ahora' : 'Desaparece cuando el contador llegue a cero'}
        </h3>

        <div className="flex flex-col md:flex-row items-center gap-6 my-6 bg-white/60 p-4 rounded-xl border border-orange-200" data-id="element-285">
          <div className="shrink-0 relative" data-id="element-286">
            <div className="absolute inset-0 bg-orange-400/30 blur-xl rounded-full scale-110" data-id="element-287" />
            <img src="https://cdn.magicpatterns.com/uploads/1RrB8D4gTwy7WrFDJu4cCh/bonosorpresalaunchboxhoy.png" alt="Lunchbox Sin Drama" className="w-32 md:w-40 h-auto rounded-lg shadow-lg relative z-10 transform -rotate-2 hover:rotate-0 transition-transform" data-id="element-288" />
          </div>

          <div className="flex-1 text-center md:text-left" data-id="element-289">
            <h4 className="text-2xl font-black text-gray-900 mb-2" data-id="element-290">
              "LUNCHBOX SIN DRAMA"
            </h4>
            <p className="text-gray-700 font-medium mb-3 leading-snug" data-id="element-291">
              30 viandas para el jardín/kinder que vuelven vacías. Sin
              ultraprocesados. Listas en 5 minutos.
            </p>
            <div className="flex items-center justify-center md:justify-start gap-2 text-lg" data-id="element-292">
              <span className="text-gray-400 line-through font-bold" data-id="element-293">
                $15 USD
              </span>
              <span className="text-gray-400 font-bold" data-id="element-294">→</span>
              <span className="bg-[#4CAF50] text-white font-black px-3 py-1 rounded-md shadow-sm" data-id="element-295">
                GRATIS si comprás ahora
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center mt-6" data-id="element-296">
          <p className="text-gray-600 font-bold mb-2 flex items-center gap-2" data-id="element-297">
            <Clock className="w-5 h-5 text-[#E65100]" data-id="element-298" />
            Te quedan:
          </p>

          <div className="flex items-center gap-2" data-id="element-299">
            <div className="bg-red-600 text-white font-black text-4xl md:text-5xl w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-xl shadow-inner animate-pulse" data-id="element-300">
              {formatNumber(timeLeft.minutes)}
            </div>
            <span className="text-red-600 font-black text-4xl md:text-5xl mb-2" data-id="element-301">
              :
            </span>
            <div className="bg-red-600 text-white font-black text-4xl md:text-5xl w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-xl shadow-inner animate-pulse" data-id="element-302">
              {formatNumber(timeLeft.seconds)}
            </div>
          </div>

          <p className="text-red-600 font-bold text-sm mt-4 text-center bg-red-50 px-4 py-2 rounded-lg border border-red-100" data-id="element-303">
            ⚠️ Pasado este tiempo el bono desaparece de tu pedido.
          </p>
        </div>
      </div>
    </motion.div>;
}